import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, CheckCircle2, XCircle, AlertCircle, Briefcase } from "lucide-react";
import useAuth from "../../contexts/AuthContext";
import Loader from "../../common_components/Loader";
import DynamicSection from "../dynamic-form/components/DynamicSection";

interface FormSection {
  id: string;
  title?: string;
  fields: unknown[];
}

interface ApplicationSchema {
  sections?: FormSection[];
}

interface Recruitment {
  title: string;
  applicationFormSchema?: ApplicationSchema;
}

interface Payment {
  amount?: number;
  utrNumber?: string;
  paymentDate?: string;
  status?: string;
}

interface TotalExperience {
  years: number;
  months: number;
  days: number;
}

interface Application {
  formData: Record<string, unknown> & { totalExperience?: TotalExperience };
  payment?: Payment;
  declaration?: { acceptedAt?: string };
  status: string;
  reviewComments?: string;
  submittedAt?: string;
}

export default function ViewApplication() {
  const { recruitmentId, applicationId } = useParams<{
    recruitmentId: string;
    applicationId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { apiRequest } = useAuth();

  const isNonFaculty = location.pathname.includes("non-faculty");
  const apiPrefix = isNonFaculty ? "/non-faculty" : "/faculty";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [application, setApplication] = useState<Application | null>(null);
  const [recruitment, setRecruitment] = useState<Recruitment | null>(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [appRes, recRes] = await Promise.all([
          apiRequest(`${apiPrefix}/application/${applicationId}`, { method: "GET" }),
          apiRequest(`${apiPrefix}/recruitment/${recruitmentId}`, { method: "GET" }),
        ]);

        if (!appRes?.application) throw new Error("Application not found");
        if (!recRes?.recruitment) throw new Error("Recruitment not found");

        setApplication(appRes.application);
        setRecruitment(recRes.recruitment);
      } catch (err: unknown) {
        console.error("Load application error:", err);
        const e = err as { message?: string };
        setError(e.message || "Failed to load application data.");
      } finally {
        setLoading(false);
      }
    }

    if (applicationId && recruitmentId) {
      loadData();
    }
  }, [apiPrefix, apiRequest, applicationId, recruitmentId]);

  if (loading) return <Loader />;

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center font-sans">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-md shadow-md space-y-4">
          <div className="text-red-600 text-center">
            <XCircle className="w-16 h-16 mx-auto mb-3" />
            <h2 className="text-xl font-bold">Error Loading Application</h2>
            <p className="text-gray-600 mt-2">{error}</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="w-full px-4 py-2 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-900 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!application || !recruitment) return null;

  const { formData, payment, declaration, status, reviewComments, submittedAt } = application;
  const expectedSchema = recruitment.applicationFormSchema;

  const getStatusColor = (val: string): string => {
    switch (val) {
      case "ACCEPTED":
        return "bg-green-100 text-green-800 border-green-200";
      case "REJECTED":
        return "bg-red-100 text-red-800 border-red-200";
      case "SUBMITTED":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPaymentStatusColor = (val: string): string => {
    switch (val) {
      case "PAID":
      case "VERIFIED":
        return "bg-green-100 text-green-800 border-green-200";
      case "PENDING":
        return "bg-amber-100 text-amber-800 border-amber-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Navigation & Actions */}
        <div className="flex items-center justify-between no-print">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-sm font-semibold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <a
            href={`${apiPrefix}/application/${recruitmentId}/${applicationId}/print`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm font-semibold transition-colors shadow-sm"
          >
            <Printer className="w-4 h-4" /> Print Application
          </a>
        </div>

        {/* Overview Header Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{recruitment.title}</h1>
              <p className="text-sm text-gray-500 mt-1">Application Reference: {applicationId}</p>
              {submittedAt && (
                <p className="text-xs text-gray-400 mt-1">
                  Submitted on: {new Date(submittedAt).toLocaleString("en-IN")}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <span className={`px-4 py-1.5 rounded-full text-xs font-bold border uppercase tracking-wider ${getStatusColor(status)}`}>
                Status: {status}
              </span>
              {payment?.amount && payment.amount > 0 && (
                <span
                  className={`px-4 py-1.5 rounded-full text-xs font-bold border uppercase tracking-wider ${getPaymentStatusColor(payment.status || "")}`}
                >
                  Payment: {payment.status}
                </span>
              )}
            </div>
          </div>

          {/* Admin Review feedback */}
          {reviewComments && (
            <div className="p-4 bg-slate-50 border rounded-xl flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="block text-sm font-bold text-gray-800">Admin Review Comments</span>
                <p className="text-sm text-gray-600 mt-1">{reviewComments}</p>
              </div>
            </div>
          )}
        </div>

        {/* Dynamic Schema Sections */}
        <div className="space-y-6">
          {expectedSchema?.sections?.map((section) => (
            <div key={section.id} className="space-y-4">
              {section.id === "experience" && formData.totalExperience && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-between shadow-xs">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    <div>
                      <span className="text-xs font-bold text-blue-800 uppercase tracking-wider block">
                        Calculated Total Work Experience
                      </span>
                      <span className="text-sm font-semibold text-blue-950 mt-0.5 block">
                        {formData.totalExperience.years} Years, {formData.totalExperience.months} Months,{" "}
                        {formData.totalExperience.days} Days
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <DynamicSection
                section={section}
                sectionData={(formData[section.id] as Record<string, unknown>) || {}}
                onChange={() => {}}
                errors={{}}
                applicationId={applicationId as string}
                disabled={true}
                recruitment={recruitment}
              />
            </div>
          ))}
        </div>

        {/* Payment & Declaration Readonly Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-6">
          <h3 className="text-xl font-bold text-gray-900 border-b pb-3">Payment & Declaration Details</h3>

          {payment?.amount && payment.amount > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-4 bg-slate-50 border rounded-xl">
                <span className="block text-xs font-semibold text-gray-500">Paid Amount</span>
                <span className="text-lg font-black text-gray-800 mt-1 block">₹{payment.amount}</span>
              </div>
              <div className="p-4 bg-slate-50 border rounded-xl">
                <span className="block text-xs font-semibold text-gray-500">UTR / Transaction ID</span>
                <span className="text-md font-mono text-gray-800 mt-1.5 block">{payment.utrNumber || "N/A"}</span>
              </div>
              <div className="p-4 bg-slate-50 border rounded-xl">
                <span className="block text-xs font-semibold text-gray-500">Payment Date</span>
                <span className="text-md font-semibold text-gray-800 mt-1.5 block">
                  {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString("en-IN") : "N/A"}
                </span>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm font-semibold">
              No Application Fee is applicable for this category.
            </div>
          )}

          <div className="p-4 bg-slate-50 border rounded-xl flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <span className="block text-sm font-bold text-gray-800">Declaration Status</span>
              <p className="text-xs text-gray-500 mt-1">
                Candidate accepted the electronic declaration on:{" "}
                {declaration?.acceptedAt ? new Date(declaration.acceptedAt).toLocaleString("en-IN") : "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
